import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { clickToCall } from "../api/calls.ts";
import {
  getThread,
  listThreadMessages,
  listThreads,
  markThreadRead,
  sendSms,
  type SmsMessage,
  type SmsThread,
} from "../api/sms.ts";

export default function SmsPage() {
  const qc = useQueryClient();

  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [mobileViewingThread, setMobileViewingThread] = useState(false);
  const [q, setQ] = useState("");

  const { data: threads, isLoading: threadsLoading } = useQuery({
    queryKey: ["sms-threads", q],
    queryFn: () => listThreads({ q: q || undefined, limit: 100 }),
    refetchInterval: 30_000,
  });

  // Auto-select first thread when none chosen — but on mobile, stay on the list view.
  useEffect(() => {
    if (!selectedThreadId && threads?.items.length) {
      setSelectedThreadId(threads.items[0].id);
    }
  }, [selectedThreadId, threads?.items]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">SMS</h1>
        <input
          className="input w-full sm:max-w-xs"
          placeholder="Search by phone or text…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="grid h-[calc(100vh-12rem)] grid-cols-1 gap-4 overflow-hidden md:grid-cols-[20rem_1fr]">
        {/* Thread list — hidden on mobile when a thread is open */}
        <aside className={`card overflow-y-auto p-0 ${mobileViewingThread ? "hidden md:block" : "block"}`}>
          {threadsLoading && <p className="p-4 text-sm text-slate-500">Loading…</p>}
          {!threadsLoading && threads?.items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">No SMS yet. Once Twilio Messaging is configured and a customer texts your number, threads appear here.</p>
          )}
          <ul className="divide-y divide-slate-100">
            {threads?.items.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={`block w-full text-left p-3 hover:bg-slate-50 ${selectedThreadId === t.id ? "bg-brand-50" : ""}`}
                  onClick={() => {
                    setSelectedThreadId(t.id);
                    setMobileViewingThread(true);
                    qc.invalidateQueries({ queryKey: ["sms-thread", t.id] });
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-mono text-sm">{t.peer_e164}</span>
                    <span className="shrink-0 text-xs text-slate-400">
                      {new Date(t.last_message_at).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="line-clamp-1 text-xs text-slate-500">{t.last_message_preview || "—"}</span>
                    {t.unread_count > 0 && (
                      <span className="shrink-0 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                        {t.unread_count}
                      </span>
                    )}
                  </div>
                  {t.status === "opted_out" && (
                    <div className="mt-1 text-[10px] uppercase text-red-700">Opted out</div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Conversation — hidden on mobile until a thread is opened */}
        <div className={`min-h-0 ${mobileViewingThread ? "block" : "hidden md:block"}`}>
          {selectedThreadId ? (
            <ThreadView
              key={selectedThreadId}
              threadId={selectedThreadId}
              onBack={() => setMobileViewingThread(false)}
            />
          ) : (
            <div className="card grid h-full place-items-center text-sm text-slate-500">Select a thread.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadView({ threadId, onBack }: { threadId: string; onBack: () => void }) {
  const qc = useQueryClient();

  const { data: thread } = useQuery({
    queryKey: ["sms-thread", threadId],
    queryFn: () => listThreadMessages(threadId),
    refetchInterval: 15_000,
  });

  const { data: threadInfo } = useQuery<SmsThread>({
    queryKey: ["sms-thread-info", threadId],
    queryFn: () => getThread(threadId),
  });

  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [thread?.items.length]);

  // Mark unread messages as read on open
  useEffect(() => {
    if (threadInfo && threadInfo.unread_count > 0) {
      markThreadRead(threadId).then(() => qc.invalidateQueries({ queryKey: ["sms-threads"] }));
    }
  }, [threadId, threadInfo, qc]);

  const send = useMutation({
    mutationFn: () => {
      if (!threadInfo) return Promise.reject(new Error("Thread info not loaded"));
      return sendSms({
        site_id: threadInfo.site_id ?? "",
        peer_e164: threadInfo.peer_e164,
        body: body.trim(),
      });
    },
    onSuccess: () => {
      setBody("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["sms-thread", threadId] });
      qc.invalidateQueries({ queryKey: ["sms-threads"] });
    },
    onError: (e) => setError((e as Error).message || "Send failed"),
  });

  const optedOut = threadInfo?.status === "opted_out";

  const [callMsg, setCallMsg] = useState<string | null>(null);
  const call = useMutation({
    mutationFn: () => {
      if (!threadInfo?.site_id) return Promise.reject(new Error("Site unknown"));
      return clickToCall({ site_id: threadInfo.site_id, to_number: threadInfo.peer_e164 });
    },
    onSuccess: (r) => { setCallMsg(r.message); setTimeout(() => setCallMsg(null), 5000); },
    onError: (e) => { setCallMsg((e as Error).message); setTimeout(() => setCallMsg(null), 5000); },
  });

  return (
    <section className="card flex h-full min-h-0 flex-col p-0">
      {callMsg && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">{callMsg}</div>
      )}
      <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <button
          type="button"
          className="-ms-2 rounded-md p-2 text-slate-700 hover:bg-slate-100 md:hidden"
          onClick={onBack}
          aria-label="Back to threads"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-mono text-sm">{threadInfo?.peer_e164 ?? "…"}</div>
          <div className="truncate text-xs text-slate-500">
            via {threadInfo?.websquad_e164 ?? "—"}
            {threadInfo?.client_id && <> · linked client</>}
          </div>
        </div>
        {optedOut && (
          <span className="shrink-0 rounded bg-red-50 px-2 py-0.5 text-xs text-red-700">Opted out</span>
        )}
        <button
          type="button"
          className="btn-secondary shrink-0 !px-2"
          title="Click-to-call this customer"
          onClick={() => call.mutate()}
          disabled={call.isPending || !threadInfo?.site_id}
        >
          <Phone className="h-4 w-4" />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {thread?.items.map((m) => <MessageBubble key={m.id} m={m} />)}
        {!thread?.items.length && (
          <p className="text-center text-sm text-slate-500">No messages in this thread yet.</p>
        )}
      </div>

      <form
        className="border-t border-slate-200 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim().length === 0) return;
          send.mutate();
        }}
      >
        {error && (
          <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        <div className="flex gap-2">
          <textarea
            className="input flex-1 resize-none"
            rows={2}
            placeholder={optedOut ? "Recipient has opted out" : "Type a message…"}
            value={body}
            disabled={optedOut || send.isPending}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                if (!optedOut && body.trim()) send.mutate();
              }
            }}
            maxLength={1600}
          />
          <button type="submit" className="btn self-end" disabled={optedOut || send.isPending || body.trim().length === 0}>
            {send.isPending ? "Sending…" : "Send"}
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">{body.length}/1600 · ⌘/Ctrl + Enter to send</p>
      </form>
    </section>
  );
}

function MessageBubble({ m }: { m: SmsMessage }) {
  const out = m.direction === "outbound";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
          out ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-900"
        }`}
      >
        <div className="whitespace-pre-wrap break-words">{m.body}</div>
        <div className={`mt-1 flex items-center justify-end gap-2 text-[10px] ${out ? "text-white/70" : "text-slate-500"}`}>
          <span>{new Date(m.created_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
          {out && <span>{m.status}</span>}
        </div>
        {m.error_message && (
          <div className={`mt-1 text-[10px] ${out ? "text-red-200" : "text-red-700"}`}>error: {m.error_message}</div>
        )}
      </div>
    </div>
  );
}
